<template>
    <div class="events" v-if="filteredEvents.length"> 
        <paginate name="event-pages" tag="div" :list="filteredEvents" class="pages" :per="page">
            <div class="event" 
                :class="['event-' + event.action.type.split('.').join('-'), 'event-' + event.action.application, 'event-display-' + getDisplay()]" 
                v-for="(event, index) in paginated('event-pages')" 
                :key="index"
                >
                <div class="event-info">
                    <div class="time">
                        <template v-if="timescale(event)">
                            {{ event.date | moment(timeFormat) }}
                        </template>
                        <template v-else>
                            &nbsp;&nbsp;--:--
                        </template>
                    </div>
                    <div class="event-context">
                        <div v-if="event.source" class="bullet" :class="event.source.category" :title="getTitle(event.source.category)"></div>
                        <div class="icon" v-if="eventIcon(event)"><font-awesome-icon :icon="eventIcon(event)"/></div>
                        <template v-if="type(event.value || event.action.context) == 'url' || (event.value || event.action.context).toString().indexOf('http') == 0">
                            <span class="title"><a :href="strip(event.value.split('w8x=')[0])" target="_blank">{{strip(event.value.split('?')[0] || event.action.context.split('?')[0])}}</a></span>
                        </template>
                        <template v-else>
                            <span class="title">{{strip(event.value || event.action.context)}}</span>
                        </template>
                    </div>
                </div>
                <div class="summary" v-if="showDetails && event.details">
                    <div class="details">
                        <div class="detail" v-for="(detail, key) in event.details" :key="'events-' + key" :class="type(detail)">
                            <div class="name">{{ actionTitle(key) }}</div>
                            <template v-if="type(detail) == 'video'"></template>
                            <template v-else-if="type(detail) == 'audio'">
                                <div class="value">
                                    <vue-plyr :options="{ controls: ['play', 'current-time', 'progress'] }">
                                        <audio>
                                            <source :src="detail" :type="type(detail, true)"/>
                                        </audio>
                                    </vue-plyr>
                                </div>
                            </template>
                            <template v-else-if="type(detail) == 'image'">
                                <div class="value">
                                    <a :href="detail" target="_blank"><img :src="detail" :alt="key"/></a>
                                </div>
                            </template>
                            <template v-else-if="type(detail) == 'date'">
                                <div class="value">
                                    {{ detail | moment('YYYY-MM-DD HH:mm') }}
                                </div>
                            </template>
                            <template v-else-if="type(detail) == 'url'">
                                <div class="value">
                                    <a :href="detail.split('w8x=')[0]" target="_blank">{{ detail.split('?')[0] }}</a>
                                </div>
                            </template>
                            <template v-else>
                                <div class="value">
                                    <span v-html="text(strip(detail))"></span>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </paginate>
        <div class="paginator" v-if="events.length > page">
            Pages<paginate-links  for="event-pages" :limit="5"></paginate-links>
        </div>
    </div>
</template>
<script>
import moment from 'moment'
import _ from 'lodash'
import mime from 'mime'
import isUrl from 'is-url'
import striptags from 'striptags'
import Autolinker from 'autolinker'

import { mapGetters, mapState } from 'vuex'

export default {
    name: 'Events',
    data () {
        return {
            paginate: ['event-pages'],
		}
    },
	props: {
        events: Array,
        showDetails: Boolean,
        display: String,
        page: {
            type: Number,
            default: 5
        },
        timeFormat: {
            type: String,
            default: 'HH:mm'
        }
    },
    computed: {
        ...mapGetters(['eventIcon', 'identities', 'timescale', 'actionTitle']),
        ...mapState({
			profile: state => state.profile.profile,
        }),
        filteredEvents() {
            return this.events.filter(this.filterEvent)
        }
    },
    methods: {
        isUrl,
        filterEvent(event) {
            return (event.value != undefined || event.details && event.details.length) && this.profile.filters[event.action.type] !== false
        },
        text(value) {
            if (typeof value != 'string') { return value }
            return Autolinker.link(value).replace(/^>.*/gm, '')
        },
        strip(value) {
            if (typeof value != 'string') { return value }
            let result = striptags(value).replace(/^\s*[\r\n]/gm, '; ')
            return result.trim()
        },
        getTitle(action) {
            let title = action.split('.').map(_.capitalize).join(' ')
            return title
        },
        getDisplay() {
            if (this.display) return this.display
            if (this.showDetails && this.events.length > 1 && _.uniq(this.events.map(event => event.action.type)).length == 1) return 'card'
            return 'section'
        },
        type(detail, full) {
            if (detail.toString().indexOf('//') == 0) detail = 'https:' + detail
            if(isUrl(detail)) {
                if (!full) {
                    let mimeType = mime.getType(detail)
                    if (!mimeType) return 'url'
                    return mimeType.split('/')[0]
                }
                return mime.getType(detail)
            } else if (moment(detail, moment.ISO_8601).isValid()) {
                return 'date'
            }
            return 'text'
        },
    }
}
</script>
<style lang="less">
@import url('../assets/variables.less');

.day .details a {
    color: @blue;
    word-break: break-all;
}

.paginate-links {
    display: inline-block;

    .number, .ellipses {
        display: inline-block;
    }

    .number a, .ellipses a {
        cursor: pointer;
        display: inline-block;
        padding: 2px @gap * 1.5;
        background-color: @gray-50;
        color: white;
        border-radius: 10px;
        margin-left: 6px;
        &:hover {
            background-color: @gray-75;
        }
    }
    .number.active a {
        background-color: @magenta;
    }
}
</style>
<style lang="less" scoped>
@import url('../assets/variables.less');
@import url('../assets/mixins.less');
.pages {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
}
.events {
    border-bottom: 1px solid @shadow-25;
    padding-bottom: @gap; 
    margin-bottom: @gap; 
    &:last-child {
        border-bottom: none;
        padding-bottom: 0; 
        margin-bottom: 0; 
    }
    .time {
        display: inline-flex;
        width: 36px;
        height: 18px;
        align-items: center;
    }
    .event {
        color: @gray-75;
        padding-right: @gap;
        & + .event {
            margin-top: @gap;
        }
    }
    .event-info {
        display: flex;
    }
    .details {
        margin-left: 38px;
    }
    .event-display-section {
        width: 100%;
        .details {
            display: flex;
            flex-direction: row;
            align-items: flex-start;
        }
        .detail {
            margin-right: @gap*2;
            // display: inline-block;
        }
    }
    .event-display-section + .event-display-section {
        padding-top: @gap; 
    }
    .event-display-card {
        width: 20%;
        flex-grow: 0;
        .event-context {
            display: inline-flex;
        }
        .event-info {
            display: flex;
            .title {
                width: calc(~"(100vw - 2*5.56rem - 2*0.69rem - 2*1.84rem) / 5 - 58px");
                text-overflow: ellipsis;
                overflow: hidden;
                display: inline-block;
                flex-grow: 1;
            }
            .detail {
                margin-bottom: @gap;
                display: block;
                width: 100%;
            }
        }
    }
    .icon {
        display: inline-block;
        width: 18px;
        flex-shrink: 0;
        margin-right: @gap / 2;
        text-align: center;
    }
    .summary {
        display: inline-flex;
        overflow-wrap: break-word;
        flex-direction: row;
        .details {
            .name {
                .caption();
            }
            .detail.text {
                a {
                    word-break: break-all;
                }
            }
            .detail.audio .value {
                max-width: 240px;
            }
            .detail.image .value {
                display: block;
                margin-bottom: @gap / 2;
                a {
                    width: calc(~"(100vw - 2*5.56rem - 2*0.69rem - 2*1.84rem) / 5 - 38px");
                    height: calc(~"(100vw - 2*5.56rem - 2*0.69rem - 2*1.84rem) / 5 - 38px");
                    display: block;
                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                }
                
            }
        }
    }
}

.bullet {
    display: inline-block;
    width: @gap*2;
    height: @gap*2;
    border-radius: 50%;
    flex-shrink: 0;
    transform: translateY(2px);
    margin-right: 4px;
    &.search {
        background-color: @red;
    }
    &.social {
        background-color: @blue;
    }
    &.direct {
        background-color: @green;
    }
    &.referral {
        background-color: @orange;
    }
    &.message {
        background-color: @pink;
    }
    &.display {
        background-color: @brick;
    }
    &.other {
        background-color: @gray;
    }
}

.paginator {
    font-size: @font-size-s;
    color: @gray-50;
    margin-right: @gap;
    padding-top: @gap;
    margin-top: @gap;
}

.events /deep/ .plyr {
    .plyr__controls {
        padding: 0;
        color: @gray-50;
        .plyr__time {
            .caption();
            transform: translateY(-2px);
        }
        .plyr__control {
            border-radius: 50%;
            background: @magenta;
            color: white;
        }
        .plyr__tab-focus,
        .plyr__control:hover {
            background: darken(@magenta, 10%);
        }
    }
    .plyr__progress__buffer {
        color: @gray-40;
    }
    input[type=range] {
        color: darken(@gray-50, 20%);
    }
}

</style>